import React, { useState } from "react";
import { Modal, Form, Input, Alert, List, Typography, Space, Spin } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { uldService } from "../../../../api/enhancedApi";

const { Text } = Typography;

/**
 * 批量更新ULD持有者的弹窗组件
 */
const HolderBatchUpdateModal = ({ visible, onCancel, onSuccess, selectedUlds = [] }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // 处理提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // 提取ULD识别码 - 根据后端API契约，需要发送识别码而不是ID
            const uldIdentifiers = selectedUlds.map(uld => uld.master.uld_identifier);
            
            // 调用批量更新API - 使用后端期望的数据格式
            const response = await uldService.batchUpdateHolder({
                uld_identifiers: uldIdentifiers,  // 后端期望字段名
                new_holder: values.holder
            });

            // 按照API_GUIDELINES规范处理响应格式
            const apiResult = response.data;
            const result = apiResult.data;
            
            if (result.success_count > 0) {
                if (result.failure_count > 0) {
                    Modal.info({
                        title: "批量更新完成",
                        content: `成功更新 ${result.success_count} 个ULD，失败 ${result.failure_count} 个`,
                    });
                } else {
                    Modal.success({
                        title: "批量更新成功",
                        content: `成功更新 ${result.success_count} 个ULD的持有者`,
                    });
                }
                
                // 重置表单并关闭弹窗
                form.resetFields();
                onSuccess && onSuccess();
            } else {
                Modal.error({
                    title: "批量更新失败",
                    content: "所有ULD更新都失败了",
                });
            }
        } catch (error) {
            console.error("批量更新持有者出错:", error);
            
            // 检查是否是表单验证错误
            if (error.name === 'ValidationError' || error.errorFields) {
                return;
            }
            
            // 更详细的错误处理 - 确保所有错误信息都是字符串
            let errorMessage = "请求失败";
            
            if (error.response?.data) {
                const errorData = error.response.data;
                
                if (typeof errorData === 'string') {
                    errorMessage = errorData;
                } else if (errorData.detail) {
                    errorMessage = String(errorData.detail);
                } else if (errorData.message) {
                    errorMessage = String(errorData.message);
                } else if (Array.isArray(errorData)) {
                    // 处理验证错误数组 - 确保每个错误都被转换为字符串
                    errorMessage = errorData.map(err => {
                        if (typeof err === 'string') return err;
                        if (err.msg) return String(err.msg);
                        if (err.message) return String(err.message);
                        // 安全地将对象转换为字符串
                        try {
                            return JSON.stringify(err);
                        } catch (jsonError) {
                            console.error("JSON stringify 失败:", jsonError);
                            return "解析错误信息失败";
                        }
                    }).join(', ');
                } else {
                    // 处理其他类型的错误数据
                    try {
                        errorMessage = JSON.stringify(errorData);
                    } catch (jsonError) {
                        console.error("JSON stringify errorData 失败:", jsonError);
                        errorMessage = "请求失败，请检查输入数据";
                    }
                }
            } else if (error.message) {
                errorMessage = String(error.message);
            }
            
            // 确保最终的错误信息是字符串
            errorMessage = String(errorMessage);
            
            Modal.error({
                title: "批量更新失败",
                content: errorMessage,
            });
        } finally {
            setLoading(false);
        }
    };

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        onCancel && onCancel();
    };

    return (
        <Modal
            title={
                <Space>
                    <UserOutlined />
                    批量更新持有者
                </Space>
            }
            open={visible}
            onOk={handleSubmit}
            onCancel={handleCancel}
            okText="确认更新"
            cancelText="取消"
            confirmLoading={loading}
            width={600}
            destroyOnHidden
        >
            <Form
                form={form}
                layout="vertical"
                preserve={false}
            >
                <Alert
                    message={`将要更新 ${selectedUlds?.length || 0} 个ULD的持有者信息`}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />

                <Form.Item
                    name="holder"
                    label="新持有者"
                    rules={[
                        { 
                            required: true, 
                            message: "请输入新的持有者" 
                        },
                        { 
                            max: 100, 
                            message: "持有者名称不能超过100个字符" 
                        },
                        {
                            validator: (_, value) => {
                                // 确保验证器始终返回字符串错误信息
                                if (!value || !value.trim()) {
                                    return Promise.reject(new Error("请输入新的持有者"));
                                }
                                if (value.length > 100) {
                                    return Promise.reject(new Error("持有者名称不能超过100个字符"));
                                }
                                return Promise.resolve();
                            }
                        }
                    ]}
                >
                    <Input
                        placeholder="请输入新的持有者名称"
                        prefix={<UserOutlined />}
                        maxLength={100}
                    />
                </Form.Item>

                <div style={{ marginTop: 16 }}>
                    <Text strong>将要更新的ULD列表：</Text>
                    <List
                        size="small"
                        style={{ 
                            marginTop: 8, 
                            maxHeight: 200, 
                            overflow: 'auto',
                            border: '1px solid #d9d9d9',
                            borderRadius: 6,
                            padding: 8
                        }}
                        dataSource={selectedUlds}
                        renderItem={(uld) => {
                            // 确保所有数据都是字符串，防止对象被直接渲染
                            const identifier = uld?.master?.uld_identifier || "未知";
                            const holder = uld?.master?.holder || "未指定";
                            
                            // 双重保险：确保数据安全
                            const safeIdentifier = String(identifier);
                            const safeHolder = String(holder);
                            
                            return (
                                <List.Item style={{ padding: '4px 0' }}>
                                    <Space>
                                        <Text code>{safeIdentifier}</Text>
                                        <Text type="secondary">
                                            当前持有者: {safeHolder}
                                        </Text>
                                    </Space>
                                </List.Item>
                            );
                        }}
                    />
                </div>
            </Form>

            {loading && (
                <div style={{ textAlign: 'center', padding: 20 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 8 }}>
                        <Text type="secondary">正在更新ULD持有者信息...</Text>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default HolderBatchUpdateModal;